import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from './useWebSocket';
import type { MinecraftServersData, MinecraftServerStats, WebSocketMessage } from '../types/statusMonitor';
import { STATUS_MONITOR_MINECRAFT_WS_URL } from '../config/statusMonitor';

/**
 * Connects to the status monitor Minecraft WebSocket after the next animation frame
 * so the main view can paint first. Tears down when `shouldConnect` is false.
 */
export function useDeferredMinecraftServers(shouldConnect: boolean) {
  const [servers, setServers] = useState<MinecraftServersData>({});
  const [socketEnabled, setSocketEnabled] = useState(false);
  const rafRef = useRef<number | null>(null);
  /** Once we have opened the socket for this page visit, keep it (e.g. advisor list refresh must not flash backup state). */
  const socketLatchedRef = useRef(false);

  useEffect(() => {
    if (!shouldConnect) {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (!socketLatchedRef.current) {
        setSocketEnabled(false);
        setServers({});
      }
      return;
    }

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setSocketEnabled(true);
      socketLatchedRef.current = true;
    });

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [shouldConnect]);

  const handleMessage = useCallback((msg: WebSocketMessage) => {
    if (msg.type === 'minecraft_init' && msg.minecraft) {
      setServers(msg.minecraft);
    }
    if (msg.type === 'minecraft_update' && msg.id && msg.data) {
      setServers((prev) => ({
        ...prev,
        [msg.id as string]: msg.data as MinecraftServerStats,
      }));
    }
    if (msg.type === 'minecraft_delete' && msg.id) {
      setServers((prev) => {
        const next = { ...prev };
        delete next[msg.id as string];
        return next;
      });
    }
  }, []);

  useWebSocket<WebSocketMessage>(STATUS_MONITOR_MINECRAFT_WS_URL, handleMessage, {
    enabled: socketEnabled,
  });

  return servers;
}
